import type { LedgerEntry } from '@vibetime/runtime';
import { type TimelineIndex, buildTimelineIndex } from './dag.js';
import { type CheckpointState, stateAt } from './state.js';

/**
 * Branch comparison: two timelines, paired by entry index. The deck
 * uses this to render side-by-side state at every scrubber position.
 */
export interface BranchCompare {
  a: { runId: string; length: number };
  b: { runId: string; length: number };
  /** Shared entry ids (runs with a common prefix from fork). */
  sharedPrefixLength: number;
  /** Index-aligned projection of state at each position. */
  pairs: BranchComparePair[];
  /** Final states for quick summary tiles. */
  final: { a: CheckpointState | null; b: CheckpointState | null };
}

export interface BranchComparePair {
  index: number;
  a: { entryId: string; kind: string } | null;
  b: { entryId: string; kind: string } | null;
}

export function compareBranches(
  entriesA: readonly LedgerEntry[],
  entriesB: readonly LedgerEntry[],
): BranchCompare {
  const indexA = buildTimelineIndex(entriesA);
  const indexB = buildTimelineIndex(entriesB);
  const length = Math.max(indexA.nodes.length, indexB.nodes.length);
  const pairs: BranchComparePair[] = [];
  let sharedPrefixLength = 0;
  let stillShared = true;
  for (let i = 0; i < length; i++) {
    const a = indexA.nodes[i];
    const b = indexB.nodes[i];
    if (stillShared && a && b && a.entry.id === b.entry.id) {
      sharedPrefixLength = i + 1;
    } else {
      stillShared = false;
    }
    pairs.push({
      index: i,
      a: a ? { entryId: a.entry.id, kind: a.entry.kind } : null,
      b: b ? { entryId: b.entry.id, kind: b.entry.kind } : null,
    });
  }
  return {
    a: { runId: indexA.runId, length: indexA.nodes.length },
    b: { runId: indexB.runId, length: indexB.nodes.length },
    sharedPrefixLength,
    pairs,
    final: {
      a: tailState(indexA),
      b: tailState(indexB),
    },
  };
}

function tailState(index: TimelineIndex): CheckpointState | null {
  const tail = index.nodes[index.nodes.length - 1];
  if (!tail) return null;
  return stateAt(index, tail.entry.id);
}
